import { useEffect } from 'react';

export const SITE_NAME = 'CSSBuy Spreadsheet';
export const FAVICON_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';
export const SITE_URL = String(import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');

export interface SeoConfig {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
}

export function toAbsoluteUrl(value: string): string {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (!SITE_URL) return value;
  return `${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function ensureCanonical(): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: 'name' | 'property', key: string, content: string | undefined): void {
  const el = ensureMeta(attr, key);
  if (content) {
    el.setAttribute('content', content);
  } else {
    el.remove();
  }
}

export function applySeo(config: SeoConfig): void {
  document.title = config.title;

  setMeta('name', 'description', config.description);
  setMeta('name', 'keywords', config.keywords);

  const canonicalEl = ensureCanonical();
  if (config.canonical) {
    canonicalEl.setAttribute('href', toAbsoluteUrl(config.canonical));
  } else {
    canonicalEl.remove();
  }

  const ogType = config.ogType ?? 'website';
  const ogImage = config.ogImage ? toAbsoluteUrl(config.ogImage) : undefined;
  const ogUrl = config.canonical ? toAbsoluteUrl(config.canonical) : undefined;

  setMeta('property', 'og:title', config.title);
  setMeta('property', 'og:description', config.description);
  setMeta('property', 'og:type', ogType);
  setMeta('property', 'og:url', ogUrl);
  setMeta('property', 'og:image', ogImage);
  setMeta('property', 'og:site_name', SITE_NAME);

  setMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
  setMeta('name', 'twitter:title', config.title);
  setMeta('name', 'twitter:description', config.description);
  setMeta('name', 'twitter:image', ogImage);
}

export function useSeo(config: SeoConfig): void {
  useEffect(() => {
    applySeo(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.title, config.description, config.keywords, config.canonical, config.ogType, config.ogImage]);
}